<template>
  <header class="header">
    <div class="inner">
      <div class="brand" @click="goHome" :title="companyName">
        <img class="logo" :src="faviconUrl" alt="ZBHJ Logo" />
        <img class="logo-name" :src="brandNameUrl" alt="ZBHJ Brand Name" />
      </div>
      <nav class="nav">
        <RouterLink to="/">首页</RouterLink>

        <!-- 解决方案 全屏下拉菜单（自动分列） -->
        <div
          class="nav-item"
          @mouseenter="showMenu('solutions')"
          @mouseleave="hideMenu"
        >
          <RouterLink to="/solutions" class="nav-link">解决方案</RouterLink>
          
          <div class="mega-menu" :class="{ open: activeMenu === 'solutions' }">
            <div class="mega-container">
              <div class="mega-left">
                <div class="title-box title-box-active">
                  <RouterLink to="/solutions">解决方案 <span>&gt;&gt;</span></RouterLink>
                </div>
              </div>

              <!-- 解决方案右侧：自动多列，每列最多4个 -->
              <div class="mega-right">
                <div 
                  class="menu-item" 
                  v-for="(col, idx) in solutionColumns" 
                  :key="idx"
                >
                  <div 
                    v-for="el in col" 
                    :key="el.id"
                    @click="handleClickSolutions(el.id)"
                  >
                    {{ el.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 产品中心 → 点击可跳转，支持分类路由 -->
        <div
          class="nav-item"
          @mouseenter="showMenu('products')"
          @mouseleave="hideMenu"
        >
          <RouterLink to="/products" class="nav-link">产品中心</RouterLink>
          
          <div class="mega-menu" :class="{ open: activeMenu === 'products' }">
            <div class="mega-container">
              <div class="mega-left">
                <!-- 软件产品 → 点击跳转到 /products?type=software -->
                <RouterLink 
                  to="/products?type=software"
                  class="title-box"
                  :class="{ active: productType === 'software' }"
                  @mouseenter="switchProduct('software')"
                >
                  软件产品 <span>&gt;&gt;</span>
                </RouterLink>

                <!-- 硬件产品 → 点击跳转到 /products?type=hardware -->
                <RouterLink 
                  to="/products?type=hardware"
                  class="title-box"
                  :class="{ active: productType === 'hardware' }"
                  @mouseenter="switchProduct('hardware')"
                >
                  硬件产品 <span>&gt;&gt;</span>
                </RouterLink>
              </div>

              <!-- 产品中心右侧：自动多列，每列最多4个 -->
              <div class="mega-right">
                <div 
                  class="menu-item" 
                  v-for="(col, idx) in currentProductColumns" 
                  :key="idx"
                >
                  <div 
                    v-for="el in col" 
                    :key="el.id" 
                    @click="handleClickProducts(el.id)"
                  >
                    {{ el.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <RouterLink to="/projects">典型案例</RouterLink>
        <RouterLink to="/news">新闻资讯</RouterLink>
        <RouterLink to="/about">关于我们</RouterLink>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useJsonResource } from '@/data/useJsonResource';
import { fallbackData } from '@/data/fallbacks';
import { SolutionItem } from '@/types';
import { useSiteConfig } from '@/data/useSiteConfig';
import { useRouter } from 'vue-router';
import { updateViews } from '@/utils/common';

// 解决方案
const { data: solutionList, refresh: refreshSolutions } = useJsonResource<SolutionItem[]>(
  'solutions',
  fallbackData.solutions as SolutionItem[]
)
// 每列最多4个，自动分多列
const solutionColumns = computed(() => {
  const list = solutionList.value || []
  const colSize = 4
  const columns = []
  for (let i = 0; i < list.length; i += colSize) {
    columns.push(list.slice(i, i + colSize))
  }
  return columns
})
const handleClickSolutions = async (id: string) => {
  await updateViews('solutions.json', id);
  await refreshSolutions();
  router.push(`/solutions/${id}`);
}

// 产品中心 
type ProductItem = {
    id: string;
    name: string;
    image: string;
    desc: string;
    type: 'software' | 'hardware';
}
const { data: productsList, refresh: refreshProducts } = useJsonResource<ProductItem[]>(
    'products',
    fallbackData.products || []
)
const softwareProducts = computed(() => {
  return (productsList.value || []).filter(item => item.type == 'software')
})
const hardwareProducts = computed(() => {
  return (productsList.value || []).filter(item => item.type == 'hardware')
})
// 产品按每列4个自动分多列
const productColSize = 4
const softwareColumns = computed(() => {
  const list = softwareProducts.value
  const cols = []
  for (let i = 0; i < list.length; i += productColSize) {
    cols.push(list.slice(i, i + productColSize))
  }
  return cols
})
const hardwareColumns = computed(() => {
  const list = hardwareProducts.value
  const cols = []
  for (let i = 0; i < list.length; i += productColSize) {
    cols.push(list.slice(i, i + productColSize))
  }
  return cols
})

const productType = ref<'software' | 'hardware'>('software')
const currentProductColumns = computed(() => {
  return productType.value === 'software' ? softwareColumns.value : hardwareColumns.value
})

const handleClickProducts = async (id: string) => {
  await updateViews('products.json', id);
  await refreshProducts();
  router.push(`/products/${id}`);
}

// 通用 
const router = useRouter()
const goHome = () => router.push('/')

const { siteConfig } = useSiteConfig()
const companyName = computed(() => siteConfig.value.contact?.companyName || '安徽中标环境科技有限公司')
const brandNameUrl = computed(() => siteConfig.value.brandNameUrl || siteConfig.value.logoUrl || '')
const brandLogo = computed(() => siteConfig.value.brandLogoUrl || siteConfig.value.logoUrl || '')
const faviconUrl = computed(() => siteConfig.value.faviconUrl || siteConfig.value.logoUrl || '')
// const hotline = computed(() => siteConfig.value.contact?.phone || '0551-65589899')
// const hotlineLink = computed(() => (hotline.value ? `tel:${hotline.value}` : 'tel:0551-65589899'))
// const email = computed(() => siteConfig.value.contact?.email || 'contact@zbhj-tech.com')
// const emailLink = computed(() => (email.value ? `mailto:${email.value}` : 'mailto:contact@zbhj-tech.com'))

const activeMenu = ref<string | null>(null)
const showMenu = (menu: string) => (activeMenu.value = menu)
const hideMenu = () => (activeMenu.value = null)

const switchProduct = (type: 'software' | 'hardware') => {
  productType.value = type
}
</script>

<style lang="scss" scss>
.header {
  border-bottom: 1px solid #e8ecef;
  background: #ffffffcc;
  backdrop-filter: saturate(120%) blur(6px);
  position: sticky;
  top: 0;
  z-index: 9999;
  --brand-height: 72px;

  .inner {
    width: min(1600px, 94%);
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: calc(var(--brand-height) + 20px);
  }

  .brand {
    font-weight: 700;
    color: #0b6fa4;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0;
  }

  .logo {
    height: var(--brand-height);
    width: auto;
    max-width: 100%;
    object-fit: contain;
    background: transparent;
    display: block;
  }

  .logo-name {
    height: var(--brand-height);
    width: auto;
    max-width: 100%;
    object-fit: contain;
    background: transparent;
    display: block;
    margin-top: 20px; /* 调整与logo的垂直对齐 */
    margin-left: -30px; /* 调整与logo的水平间距 */
  }

  .nav {
    display: flex;
    gap: 68px;
    align-items: center;
    position: static;

    a {
      font-family: PingFangSC, PingFang SC;
      font-weight: 400;
      font-size: 20px;
      color: #333;
      text-decoration: none;

      &.router-link-active {
        color: #0b6fa4;
      }
    }
  }

  .nav-item {
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
  }

  .mega-menu {
    position: fixed;
    left: 0;
    top: 92px;
    width: 100%;
    height: 0;
    overflow: hidden;
    background: rgba(230, 245, 255, 0.6);
    transition: height 0.35s ease;
    z-index: 999;

    &.open {
      height: 220px;
    }

    .mega-container {
      width: 100%;
      height: 220px;
      display: flex;
      align-items: center;
      gap: 40px;
    }

    .mega-left {
      width: 65%;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      gap: 16px;

      .title-box {
        font-size: 18px;
        padding: 8px 24px 8px 40px;
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        text-decoration: none;
        transition: all 0.2s ease;
        background: transparent;
        color: #333;
      }

      .title-box-active {
        background: linear-gradient(90deg, #00b4e6, #0075c2) !important;
        color: #fff !important;
        a {
          color: #fff !important; /* 强制内部文字白色 */
        }
      }

      .title-box:hover,
      .title-box.active {
        background: linear-gradient(90deg, #00b4e6, #0075c2) !important;
        color: #fff !important;
      }
    }

    .mega-right {
      width: 35%;
      height: 100%;
      padding: 20px;
      background: #EAF2F8;
      display: flex;
      gap: 80px; /* 多列自动间距 */
    }

    .menu-item {
      display: flex;
      flex-direction: column;
      gap: 18px;

      div {
        font-weight: 400;
        font-size: 16px;
        color: #333;
        cursor: pointer;
        white-space: nowrap;
        transition: color 0.2s;

        &:hover {
          color: #0075C2;
        }
      }
    }
  }
}

// 移动端
@media (max-width: 768px) {
  .header {
    --brand-height: 48px;
    .inner {
      height: auto;
      padding: 12px 0;
      flex-direction: column;
      align-items: flex-start;
    }
    .nav {
      flex-wrap: wrap;
    }
    .mega-menu {
      display: none;
    }
  }
}
</style>